import React, { ReactNode, useEffect, useState } from 'react';
import { displayMoneyFormat } from '../../util/util';
import { Button } from '../../components/form/button';
import { PaymentModal } from '../payment/payment-modal';
import { Invoice, InvoiceStatus, InvoiceTaxMeta, LineItem } from '../../clients/quipli-service/invoice/invoice';
import { Location } from '../../clients/quipli-service/location/location';
import './collectibles.scss';
import { StatusBadge, StatusBadgeType } from '../../components/status-badge/status-badge';
import classNames from 'classnames';
import { Card } from '../../components/card/card';
import _ from 'lodash';
import { Payment, PaymentStatus } from '../../clients/quipli-service/payment/payment';
import Icon from '../../components/icons/icon';
import { QuipliLink } from '../../components/link/quipli-link';
import { useAdmin } from '../../hooks/use-admin-context';
import { Dropdown } from '../../components/dropdown/dropdown';
import { Typography } from '../../components/typography/typography';
import { RefundPaymentModal } from '../order/invoices/refund-payment-modal';
import { useInvoiceApiV1 } from '../../clients/quipli-service/invoice/invoice-api-v1';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { toast } from '../../util/toasts';
import { DateTime } from 'luxon';
import { EditLineItemModal } from './components/edit-line-item-modal';
import { useOrderApiV1 } from '../../clients/quipli-service/order/order-api-v1';
import { AddLineItemModal } from '../order/invoices/add-line-item-modal';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import { AccountingPeriodClose } from '../../clients/quipli-service/accounting/accounting';
import { Checkbox } from '../../components/checkbox/checkbox';
import { SortIcon } from '../../components/table/desktop-data-table';
import { PaymentHistory } from './components/payment-history';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { Modal } from '../../components/modal/modal';
import { SelectForm } from '../../components/select-form/select-form';
import { User } from '../../clients/quipli-service/user/user';
import { NotificationType } from '../../clients/quipli-service/notfication/notification';
import { ConfirmPaymentModal } from '../payment/confirm-payment-modal';
import { useRepresentativeApi } from '../../clients/quipli-service/representatives/representative-api';
import { Representative } from '../../clients/quipli-service/representatives/representative';
import { Constants } from '../../constants/constants';
import { PdfPrinter } from '../../components/pdf-printer/pdf-printer';
import FileSaver from 'file-saver';

export type InvoiceTableColumns =
  | 'InvoiceNumber'
  | 'Customer'
  | 'Location'
  | 'DueDate'
  | 'Date'
  | 'Status'
  | 'DaysPastDue'
  | 'Tax'
  | 'Total'
  | 'Payments'
  | 'Balance'
  | 'Actions'
  | 'CheckBoxColumn';

export const DEFAULT_INVOICE_TABLE_COLUMNS: InvoiceTableColumns[] = [
  'InvoiceNumber',
  'Customer',
  'Location',
  'DueDate',
  'Date',
  'Status',
  'Tax',
  'Total',
  'Payments',
  'Balance',
  'Actions',
];

export const InvoiceList = ({
  invoices,
  locations,
  currency,
  refresh,
  enabledColumns = DEFAULT_INVOICE_TABLE_COLUMNS,
  enableInvoiceLink = true,
  enablePaymentNotificationAction = true,
  sortable = false,
  sort,
  showCycleBillingActions = false,
  authorizedRep = undefined,
  displayTaxByJurisdiction,
  taxInclusive,
  taxLabel,
}: {
  invoices: Invoice[];
  locations?: Location[];
  currency: string;
  refresh: (sortBy?: 'date' | 'dueDate' | 'invoiceNumber') => Promise<any>;
  enabledColumns?: InvoiceTableColumns[];
  enableInvoiceLink?: boolean;
  enablePaymentNotificationAction?: boolean;
  sortable?: boolean;
  sort?: { field: 'date' | 'dueDate' | 'invoiceNumber'; direction: 'asc' | 'desc' };
  showCycleBillingActions?: boolean;
  authorizedRep?: Representative | undefined;
  displayTaxByJurisdiction: boolean;
  taxInclusive: boolean;
  taxLabel?: string;
}) => {
  const { deleteLineItem, voidInvoice, downloadInvoices } = useInvoiceApiV1();
  const { getCurrentAccountingPeriodClose } = useAccountingApiV1();
  const { regenerateInvoiceForCycle } = useOrderApiV1();
  const { sendNotification, sendInvoice } = useAPI();

  const { contextPath } = useAdmin();

  const [currentAccountingPeriodClose, setCurrentAccountingPeriodClose] = useState<AccountingPeriodClose>();
  const [invoiceToPay, setInvoiceToPay] = useState<Invoice>();
  const [invoiceToPrint, setInvoiceToPrint] = useState<Invoice>();
  const [paymentStepConfirmed, setPaymentStepConfirmed] = useState<boolean>(false);
  const [invoiceToIssueRefund, setInvoiceToIssueRefund] = useState<Invoice>();
  const [selectedInvoiceAddLineItem, setSelectedInvoiceAddLineItem] = useState<Invoice>();
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | undefined>(
    invoices.length === 1 ? invoices[0].id : undefined,
  );
  const [confirmModal, setConfirmModal] = useState<ReactNode | undefined>();
  const [lineItemToEdit, setLineItemToEdit] = useState<LineItem>();
  const [invoiceIdToResend, setInvoiceIdToResend] = useState<string>();
  const today = DateTime.now().startOf('day');

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [invoiceIdToNotify, setInvoiceIdToNotify] = useState<string | undefined>();
  const { hasPermissions } = useAdmin();

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);

  useEffect(() => {
    getCurrentAccountingPeriodClose()
      .then((acp) => setCurrentAccountingPeriodClose(acp))
      .catch((e) => {
        console.log(e);
        toast('Error loading current accounting period close', 'error');
      });
  }, []);

  useEffect(() => {
    if (!invoiceIdToResend) return;

    try {
      sendInvoice(invoiceIdToResend).then(() => toast('Invoice email sent'));
      setInvoiceIdToResend(undefined);
    } catch {
      toast('Error resending invoice', 'error');
    }
  }, [invoiceIdToResend]);

  async function onModalClose(payments?: Payment[] | LineItem[]) {
    if (payments) {
      await refresh();
    }
    setInvoiceToPay(undefined);
    setSelectedInvoiceAddLineItem(undefined);
    setInvoiceToIssueRefund(undefined);
  }

  async function onDeleteLineItem(lineItem: LineItem) {
    try {
      await deleteLineItem(lineItem.invoiceId, lineItem.id);
      toast('Line item deleted successfully');
    } catch (e: any) {
      toast('Failed to delete line item', 'error');
    } finally {
      setConfirmModal(undefined);
      await refresh();
    }
  }

  async function onRegenerateInvoice(invoice: Invoice) {
    try {
      const sections = invoice.invoiceNumber!.split('-');
      await regenerateInvoiceForCycle(parseInt(invoice.typeId), parseInt(sections[sections.length - 1]));
      toast('Invoice regenerated successfully');
    } catch (e: any) {
      toast('Failed to regenerated invoice', 'error');
    } finally {
      setConfirmModal(undefined);
      await refresh();
    }
  }

  async function onVoidInvoice(invoice: Invoice) {
    try {
      await voidInvoice(invoice.id);
      toast('Invoice voided successfully');
    } catch (e: any) {
      toast('Failed to void invoice', 'error');
    } finally {
      setConfirmModal(undefined);
      await refresh();
    }
  }

  async function sendPaymentNotification(invoiceId: string) {
    const invoice = invoices.find((i) => i.id === invoiceId);
    setInvoiceIdToNotify(invoiceId);
    const customer = invoice?.customer;
    setSelectedUser(customer);
    setIsModalOpen(true);
  }

  async function downloadSelectedInvoicesPDF() {
    try {
      const file = await downloadInvoices(selectedInvoiceIds);
      const fileType = 'application/pdf';

      const data = new Blob([file], { type: fileType });

      FileSaver.saveAs(data, `Invoices.pdf`);
    } catch (error: any) {
      toast(error.message || 'Failed to generate invoices', 'error');
    }
  }

  return (
    <>
      {selectedInvoiceIds.length > 0 && (
        <div className="relative w-full">
          <div className="table-checkbox-actions rounded-[12px]">
            <div className="flex pl-2">
              <Checkbox
                type="white"
                isChecked={selectedInvoiceIds.length > 0}
                icon={<Icon icon="Minus" className="w-[14px]" stroke="var(--grey-900)" strokeWidth={4} />}
                onChange={(isChecked) => {
                  if (!isChecked) {
                    setSelectedInvoiceIds([]);
                  }
                }}
              />
              <Typography type="text-md" className="text-[white]">
                {selectedInvoiceIds.length} items selected
              </Typography>
            </div>
            <div className="pr-2">
              <div className="flex gap-3">
                <div className="cursor-pointer mr-2" onClick={() => downloadSelectedInvoicesPDF()}>
                  <Typography type="text-md" className="text-[white] flex justify-center justify-items-center gap-1">
                    <Icon icon="Download" stroke="white" />
                    Download as PDF
                  </Typography>
                </div>
              </div>
            </div>
            <div className="table-checkbox-actions-vector">
              <svg width="20" height="8" viewBox="0 0 20 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10 8C7 8 4.0001 -1.39876e-06 6.99382e-07 -1.74846e-06L20 0C16.0251 -3.47497e-07 13 8 10 8Z"
                  fill="#1D2939"
                ></path>
              </svg>
            </div>
          </div>
        </div>
      )}

      <div className="invoice-table">
        {(invoices.length || 0) > 0 && (
          <table className="w-full">
            <thead>
              <tr>
                {enabledColumns.indexOf('CheckBoxColumn') >= 0 && (
                  <th>
                    <Checkbox
                      type="white"
                      isChecked={
                        selectedInvoiceIds &&
                        selectedInvoiceIds.length > 0 &&
                        selectedInvoiceIds.length === invoices.length
                      }
                      onChange={(isChecked) => {
                        if (isChecked) {
                          setSelectedInvoiceIds && setSelectedInvoiceIds(invoices.map((invoice) => invoice.id));
                        } else {
                          setSelectedInvoiceIds([]);
                        }
                      }}
                    />
                  </th>
                )}
                {enabledColumns.indexOf('InvoiceNumber') >= 0 && (
                  <th className="text-left">
                    <span
                      className={classNames('flex gap-1', { sortable: 'cursor-pointer' })}
                      onClick={() => sortable && refresh('invoiceNumber')}
                    >
                      Invoice No.
                      {sortable && (
                        <SortIcon currentDirection={sort?.field === 'invoiceNumber' ? sort.direction : undefined} />
                      )}
                    </span>
                  </th>
                )}
                {enabledColumns.indexOf('Location') >= 0 && <th className="text-left">Location</th>}
                {enabledColumns.indexOf('Customer') >= 0 && <th className="text-left">Customer</th>}
                {enabledColumns.indexOf('DueDate') >= 0 && (
                  <th>
                    <span
                      className={classNames('flex gap-1', { sortable: 'cursor-pointer' })}
                      onClick={() => sortable && refresh('dueDate')}
                    >
                      Due Date
                      {sortable && (
                        <SortIcon currentDirection={sort?.field === 'dueDate' ? sort.direction : undefined} />
                      )}
                    </span>
                  </th>
                )}
                {enabledColumns.indexOf('Date') >= 0 && (
                  <th>
                    <span
                      className={classNames('flex gap-1', { sortable: 'cursor-pointer' })}
                      onClick={() => sortable && refresh('date')}
                    >
                      Date
                      {sortable && <SortIcon currentDirection={sort?.field === 'date' ? sort.direction : undefined} />}
                    </span>
                  </th>
                )}
                {enabledColumns.indexOf('Status') >= 0 && <th>Status</th>}
                {enabledColumns.indexOf('DaysPastDue') >= 0 && <th className="text-center">Days Past Due</th>}
                {enabledColumns.indexOf('Tax') >= 0 && <th className="text-right">Tax</th>}
                {enabledColumns.indexOf('Total') >= 0 && <th className="text-right">Total</th>}
                {enabledColumns.indexOf('Payments') >= 0 && <th className="text-right">Payments</th>}
                {enabledColumns.indexOf('Balance') >= 0 && <th className="text-right">Balance</th>}
                {enabledColumns.indexOf('Actions') >= 0 && <th />}
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const totalPayments = _.sum(
                  invoice.payments
                    .filter((p) => p.status === PaymentStatus.succeeded && p.amount > 0)
                    .map((_) => _.invoicePayments.find((_) => _.invoiceId === invoice.id)!)
                    .map((p) => p.amount),
                );
                const totalRefunds = _.sum(
                  invoice.payments
                    .filter((p) => p.status === PaymentStatus.succeeded && p.amount < 0)
                    .map((_) => _.invoicePayments.find((_) => _.invoiceId === invoice.id)!)
                    .map((p) => p.amount),
                );
                const netPayments = _.sum(
                  invoice.payments
                    .filter((p) => p.status === PaymentStatus.succeeded)
                    .map((_) => _.invoicePayments.find((_) => _.invoiceId === invoice.id)!)
                    .map((p) => p.amount),
                );
                let invoiceLink: string;
                switch (invoice.type) {
                  case 'Order':
                    invoiceLink = `orders/view/${invoice.typeId}`;
                    break;
                  case 'WorkOrder':
                    invoiceLink = `workorders/view/${invoice.typeId}`;
                    break;
                  case 'PurchaseOrder':
                    invoiceLink = `purchase-orders/${invoice.typeId}`;
                }
                return (
                  <React.Fragment key={invoice.id}>
                    <tr
                      className={classNames('invoice-row', { expanded: expandedInvoiceId === invoice.id })}
                      onClick={() =>
                        setExpandedInvoiceId((invoiceId) => (invoiceId === invoice.id ? undefined : invoice.id))
                      }
                    >
                      {enabledColumns.indexOf('CheckBoxColumn') >= 0 && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            type="white"
                            isChecked={selectedInvoiceIds?.includes(invoice.id)}
                            onChange={(isChecked) => {
                              if (isChecked) {
                                setSelectedInvoiceIds &&
                                  setSelectedInvoiceIds((prevState: string[]) => [...prevState, invoice.id]);
                              } else {
                                setSelectedInvoiceIds &&
                                  setSelectedInvoiceIds((prevState: string[]) =>
                                    prevState.filter((elem: string) => elem !== invoice.id),
                                  );
                              }
                            }}
                          />
                        </td>
                      )}
                      {enabledColumns.indexOf('InvoiceNumber') >= 0 && (
                        <td className="text-left flex gap-2 items-center mt-2.5">
                          {enableInvoiceLink ? (
                            <QuipliLink
                              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
                              to={invoiceLink}
                            >
                              {invoice.invoiceNumber}
                            </QuipliLink>
                          ) : (
                            invoice.invoiceNumber
                          )}
                          {invoice.locked ? <i className="fa fa-lock text-[var(--grey-500)]" /> : undefined}
                          <i
                            className={classNames('fa text-[var(--grey-500)]', {
                              'fa-angle-up': expandedInvoiceId === invoice.id,
                              'fa-angle-down': expandedInvoiceId !== invoice.id,
                            })}
                          />
                        </td>
                      )}
                      {enabledColumns.indexOf('Location') >= 0 && (
                        <td className="text-left">{locations?.find((l) => l.id === invoice.locationId)?.name}</td>
                      )}
                      {enabledColumns.indexOf('Customer') >= 0 && (
                        <td className="text-left">
                          <QuipliLink
                            className="underline underline-offset-1 text-[var(--quipli-black-800)]"
                            to={`users/customer_form/${invoice.customer.id}`}
                          >
                            {invoice.customer.name}
                          </QuipliLink>
                        </td>
                      )}
                      {enabledColumns.indexOf('DueDate') >= 0 && (
                        <td className="text-left">{invoice.dueDate?.toFormat('D')}</td>
                      )}
                      {enabledColumns.indexOf('Date') >= 0 && (
                        <td className="text-left">{invoice.date?.toFormat('D')}</td>
                      )}
                      {enabledColumns.indexOf('Status') >= 0 && (
                        <td className="text-center">{InvoiceStatusBadge(invoice)}</td>
                      )}
                      {enabledColumns.indexOf('DaysPastDue') >= 0 && (
                        <td className="text-center text-red-500">
                          {invoice.dueDate &&
                          invoice.status === InvoiceStatus.Approved &&
                          invoice.balance > 0 &&
                          today
                            .setZone(locations?.find((l) => l.id === invoice.locationId)?.timezone || 'UTC')
                            .diff(invoice.dueDate)
                            .as('days') >= 1
                            ? `${Math.floor(
                                today
                                  .setZone(locations?.find((l) => l.id === invoice.locationId)?.timezone || 'UTC')
                                  .diff(invoice.dueDate)
                                  .as('days'),
                              )} days`
                            : ''}
                        </td>
                      )}
                      {enabledColumns.indexOf('Tax') >= 0 && (
                        <td className="text-right">{displayMoneyFormat(invoice.tax, currency)}</td>
                      )}
                      {enabledColumns.indexOf('Total') >= 0 && (
                        <td className="text-right">{displayMoneyFormat(invoice.total, currency)}</td>
                      )}
                      {enabledColumns.indexOf('Payments') >= 0 && (
                        <td className="text-right">
                          {netPayments !== 0 ? displayMoneyFormat(netPayments, currency) : ''}
                        </td>
                      )}
                      {enabledColumns.indexOf('Balance') >= 0 && (
                        <td className="text-right">
                          {invoice.balance !== 0 && invoice.status !== InvoiceStatus.Void
                            ? displayMoneyFormat(invoice.balance, currency)
                            : ''}
                        </td>
                      )}
                      {enabledColumns.indexOf('Actions') >= 0 && (
                        <td className="text-right flex justify-end" onClick={() => 0}>
                          {invoice.balance > 0 &&
                            invoice.status !== InvoiceStatus.Void &&
                            !invoice.customer.paymentBan &&
                            invoice.documentType == 'Invoice' && (
                              <Button
                                disabled={!hasPermissions(Constants.Permissions.Refund_Upcharge_Order)}
                                className="button button-secondary m-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInvoiceToPay(invoice);
                                }}
                              >
                                <span className="min-w-[4rem] font-semibold">Pay</span>
                              </Button>
                            )}

                          {(invoice.balance < 0 ||
                            (invoice.status === InvoiceStatus.Void &&
                              _.sum(
                                invoice.payments
                                  .filter((_) => _.status == 'succeeded')
                                  .map((_) => _.invoicePayments.find((_) => _.invoiceId === invoice.id)!)
                                  .map((p) => p.amount),
                              ) >= 0.01)) &&
                            invoice.documentType == 'Invoice' && (
                              <Button
                                disabled={!hasPermissions(Constants.Permissions.Refund_Upcharge_Order)}
                                className="button button-primary m-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInvoiceToIssueRefund(invoice);
                                }}
                              >
                                <span className="min-w-[4rem] font-semibold">Issue Refund</span>
                              </Button>
                            )}

                          {/* additional ellipses dropdown options */}
                          <div>
                            <Dropdown
                              placement="bottom-end"
                              trigger={() => (
                                <Button className="button-tertiary" data-cy={`invoice_menu`}>
                                  <Icon icon="DotsHorizontal" />
                                </Button>
                              )}
                            >
                              <div
                                className="min-w-full flex justify-start items-center gap-2 px-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInvoiceToPrint(invoice);
                                }}
                              >
                                <Typography className="flex justify-center justify-items-center gap-1" type="text-sm">
                                  <Icon icon="Printer" stroke="black" /> Print
                                </Typography>
                              </div>
                              <div
                                className="min-w-full flex justify-start items-center gap-2 px-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(
                                    invoice.type === 'Order'
                                      ? `${contextPath}quipli-service/yorent/v2/orders/${invoice.typeId}/invoices/${invoice.id}`
                                      : `${contextPath}quipli-service/v1/work-order/${invoice.typeId}/invoices/${invoice.id}`,
                                  );
                                }}
                              >
                                <Typography className="flex justify-center justify-items-center gap-1" type="text-sm">
                                  <Icon icon="Download" stroke="black" /> Download
                                </Typography>
                              </div>

                              <div
                                className="min-w-full flex justify-start items-center gap-2 px-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInvoiceIdToResend(invoice.id);
                                }}
                              >
                                <Typography className="flex justify-center justify-items-center gap-1" type="text-sm">
                                  <Icon icon="Envelope" stroke="black" /> Email Invoice
                                </Typography>
                              </div>

                              {(currentAccountingPeriodClose && invoice.documentType == 'CreditMemo'
                                ? invoice.date! > currentAccountingPeriodClose.closeDateTime
                                : true) && (
                                <>
                                  <div
                                    className="min-w-full flex justify-start items-center gap-2 px-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedInvoiceAddLineItem(invoice);
                                    }}
                                  >
                                    <Typography
                                      className="flex justify-center justify-items-center gap-1"
                                      type="text-sm"
                                    >
                                      <Icon icon="AddDocument" stroke="black" /> Add Line Item
                                    </Typography>
                                  </div>
                                </>
                              )}

                              {enablePaymentNotificationAction && invoice.balance > 0 && (
                                <div
                                  className="min-w-full flex justify-start items-center gap-2 px-0 hover:bg-[var(--grey-100)]"
                                  onClick={() => sendPaymentNotification(invoice.id)}
                                >
                                  <Typography
                                    className="flex justify-center justify-items-center gap-1 text-[var(--quipli-black-800)]"
                                    type="text-sm"
                                  >
                                    Send Payment Notification
                                  </Typography>
                                </div>
                              )}

                              {(!currentAccountingPeriodClose ||
                                (invoice.date && currentAccountingPeriodClose.closeDateTime < invoice.date)) &&
                                showCycleBillingActions &&
                                invoice.invoiceNumber && (
                                  <>
                                    <div
                                      className="min-w-full flex justify-start items-center gap-2 px-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmModal(
                                          <ConfirmModal
                                            isOpen={true}
                                            close={() => setConfirmModal(false)}
                                            onConfirm={() => onRegenerateInvoice(invoice)}
                                            prompt={`Are you sure you want regenerate invoice ${invoice.invoiceNumber}?`}
                                          />,
                                        );
                                      }}
                                    >
                                      <Typography
                                        className="flex justify-center justify-items-center gap-1"
                                        type="text-sm"
                                      >
                                        Regenerate
                                      </Typography>{' '}
                                    </div>
                                    {invoice.status !== InvoiceStatus.Void && (
                                      <div
                                        className="min-w-full flex justify-start items-center gap-2 px-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setConfirmModal(
                                            <ConfirmModal
                                              isOpen={true}
                                              close={() => setConfirmModal(false)}
                                              onConfirm={() => onVoidInvoice(invoice)}
                                              prompt={`Are you sure you want void invoice ${invoice.invoiceNumber}?`}
                                            />,
                                          );
                                        }}
                                      >
                                        <Typography
                                          className="flex justify-center justify-items-center gap-1"
                                          type="text-sm"
                                        >
                                          Void
                                        </Typography>{' '}
                                      </div>
                                    )}
                                  </>
                                )}
                            </Dropdown>
                          </div>
                        </td>
                      )}
                    </tr>
                    {invoice.id === expandedInvoiceId && (
                      <tr className={classNames('invoice-detail', { expanded: expandedInvoiceId === invoice.id })}>
                        <td colSpan={100}>
                          <div className="p-2 pt-0 flex flex-column gap-2">
                            <Card className="w-full">
                              <h6 className="p-2">Invoice Details</h6>
                              <table className="w-full">
                                <thead>
                                  <tr>
                                    <th className="text-left">Name</th>
                                    <th className="text-left">Description</th>
                                    <th className="text-center">Unit Price</th>
                                    <th className="text-center">Quantity</th>
                                    <th className="text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {invoice.lineItems
                                    .filter((_) => {
                                      return _.type != 'Tax';
                                    })
                                    .map((li) => (
                                      <tr key={li.id}>
                                        <td className="text-left">{li.name}</td>
                                        <td className="text-left">
                                          {li.description?.split('\n').map((line, i) => (
                                            <div key={i}>{line}</div>
                                          ))}
                                        </td>
                                        <td className="text-center">{displayMoneyFormat(li.unitPrice, currency)}</td>
                                        <td className="text-center">{li.quantity}</td>
                                        <td className="text-right">
                                          {displayMoneyFormat(
                                            taxInclusive
                                              ? li.total +
                                                  ((li.meta?.tax as InvoiceTaxMeta[])?.reduce((a, b) => {
                                                    return a + b.amount;
                                                  }, 0) ?? 0)
                                              : li.total,
                                            currency,
                                          )}{' '}
                                          {taxInclusive
                                            ? li.taxable && invoice.customer.chargeTax
                                              ? `(${taxLabel} incl.)`
                                              : `(No ${taxLabel} incl.)`
                                            : ''}
                                        </td>
                                        {li.type === 'LineItem' && (
                                          <td className="text-right flex justify-end items-center">
                                            <span className="hover:cursor-pointer">
                                              <Icon
                                                icon="Edit"
                                                stroke="var(--grey-500)"
                                                onClick={() => setLineItemToEdit(li)}
                                              />
                                            </span>
                                            <span className="hover:cursor-pointer">
                                              <Icon
                                                icon="Trash"
                                                stroke="var(--grey-500)"
                                                onClick={() =>
                                                  setConfirmModal(
                                                    <ConfirmModal
                                                      isOpen={true}
                                                      close={() => setConfirmModal(false)}
                                                      onConfirm={() => onDeleteLineItem(li)}
                                                      prompt={`Are you sure you want to delete this line item for ${displayMoneyFormat(
                                                        li.total,
                                                        currency,
                                                      )}?`}
                                                    />,
                                                  )
                                                }
                                              />
                                            </span>
                                          </td>
                                        )}
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </Card>
                            <div className="flex gap-2">
                              <Card className="grow">
                                <PaymentHistory payments={invoice.payments} refresh={refresh} invoice={invoice} />
                              </Card>
                              <Card className="p-4 min-w-[20%] totals-card">
                                <ul className="list-none p-0 flex flex-col gap-2">
                                  <li className="flex gap-12 justify-between">
                                    <span>Subtotal</span>
                                    <span className="text-right">{displayMoneyFormat(invoice.subtotal, currency)}</span>
                                  </li>
                                  {displayTaxByJurisdiction ? (
                                    invoice.lineItems
                                      .filter((li) => li.type === 'Tax' && li.total !== 0)
                                      .map((li) => {
                                        let description = `Tax ${li.description ?? li.name}`;
                                        if (li.description) {
                                          const match = li.description.match(/^Jurisdiction: (.*)Rate: (.*)$/);
                                          if (match) {
                                            description = `${match[1]} (${match[2]})`;
                                          }
                                        }
                                        return (
                                          <li className="flex gap-4 justify-between grey">
                                            <span>{description}</span>
                                            <span className="text-right">{displayMoneyFormat(li.total, currency)}</span>
                                          </li>
                                        );
                                      })
                                  ) : (
                                    <li className="flex gap-4 justify-between grey">
                                      <span>
                                        Tax ({(invoice.taxRate * 100).toFixed(2).replace(/[.,]00$/, '')}
                                        %)
                                      </span>
                                      <span className="text-right">{displayMoneyFormat(invoice.tax, currency)}</span>
                                    </li>
                                  )}
                                  <li className="flex gap-4 justify-between bold">
                                    <span>Total</span>
                                    <span className="text-right">{displayMoneyFormat(invoice.total, currency)}</span>
                                  </li>
                                  {totalPayments !== 0 && (
                                    <li className="flex gap-4 justify-between grey">
                                      <span>Payment(s)</span>
                                      <span className="text-right">{displayMoneyFormat(totalPayments, currency)}</span>
                                    </li>
                                  )}
                                  {totalRefunds !== 0 && (
                                    <li className="flex gap-4 justify-between grey">
                                      <span>Refund(s)</span>
                                      <span className="text-right">{displayMoneyFormat(totalRefunds, currency)}</span>
                                    </li>
                                  )}
                                  <li className="flex gap-4 justify-between bold">
                                    <span>Balance Due</span>
                                    <span className="text-right">
                                      {invoice.status !== InvoiceStatus.Void
                                        ? displayMoneyFormat(invoice.balance, currency)
                                        : '-'}
                                    </span>
                                  </li>
                                </ul>
                              </Card>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {invoiceToPay && !paymentStepConfirmed && (
          <ConfirmPaymentModal
            invoice={invoiceToPay}
            onConfirm={() => setPaymentStepConfirmed(true)}
            onCancel={async () => {
              setInvoiceToPay(undefined);
              await onModalClose([]);
            }}
            currency={currency}
          />
        )}
        {invoiceToPay && paymentStepConfirmed && (
          <PaymentModal
            isOpen={!!invoiceToPay}
            close={onModalClose}
            invoice={invoiceToPay}
            currency={currency}
            user={invoiceToPay.customer}
          />
        )}
        <PdfPrinter
          pdf={
            invoiceToPrint &&
            (invoiceToPrint.type === 'Order'
              ? `${contextPath}quipli-service/yorent/v2/orders/${invoiceToPrint.typeId}/invoices/${invoiceToPrint.id}`
              : `${contextPath}quipli-service/v1/work-order/${invoiceToPrint.typeId}/invoices/${invoiceToPrint.id}`)
          }
          onComplete={() => setInvoiceToPrint(undefined)}
        />

        {invoiceToIssueRefund && (
          <RefundPaymentModal
            isOpen={!!invoiceToIssueRefund}
            close={onModalClose}
            invoice={invoiceToIssueRefund}
            currency={currency}
          />
        )}
        {selectedInvoiceAddLineItem && (
          <AddLineItemModal
            invoice={selectedInvoiceAddLineItem}
            isOpen={!!selectedInvoiceAddLineItem}
            close={onModalClose}
            currency={currency}
          />
        )}
        {confirmModal}
        {lineItemToEdit && (
          <EditLineItemModal
            lineItem={lineItemToEdit}
            isOpen={!!lineItemToEdit}
            close={() => {
              setLineItemToEdit(undefined);
              refresh();
            }}
            currency={currency}
            invoice={invoices.find((i) => i.id === lineItemToEdit.invoiceId)!}
          />
        )}
        {isModalOpen && (
          <NotifyEmailModal
            isOpen={isModalOpen}
            close={() => {
              setInvoiceIdToNotify(undefined);
              setSelectedUser(undefined);
              setIsModalOpen(false);
            }}
            user={selectedUser}
            sendMethod={async (email) =>
              invoiceIdToNotify &&
              (await sendNotification({
                type: NotificationType.clickToPay,
                invoiceId: invoiceIdToNotify,
                emailOverride: email,
              }))
            }
            authorizedRep={authorizedRep}
          />
        )}
      </div>
    </>
  );
};

type notifyEmailProps = {
  isOpen: boolean;
  close: () => void;
  sendMethod: (e: any) => any;
  user?: User;
  authorizedRep?: Representative | undefined;
};

const NotifyEmailModal = (props: notifyEmailProps) => {
  const { isOpen, close, user, authorizedRep, sendMethod } = props;

  const { getRepresentatives } = useRepresentativeApi();
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [selectedDropdown, setSelectedDropdown] = useState<{ value: string; label: string } | null>();
  const [manualEmail, setManualEmail] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<'dropdown' | 'free-text'>('dropdown');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const repToLabel = (rep: Representative) => rep.name + ' - ' + rep.email;

  // Loads and sets the drop-down options based on the user's structure type
  useEffect(() => {
    if (isOpen && user) {
      if (user.structureType === 'company') {
        setIsLoading(true);
        getRepresentatives(user.id).then((res) => {
          setRepresentatives(res);
          const repFromOrder = res.find((r) => r.id === authorizedRep?.id);
          if (repFromOrder) {
            setSelectedDropdown({
              value: repFromOrder.email ?? '',
              label: repToLabel(repFromOrder),
            });
          }
          setIsLoading(false);
        });
      } else {
        setSelectedDropdown({
          value: user.email,
          label: 'primary - ' + user.email,
        });
      }
    }
  }, [isOpen, user]);

  const dropdownOptions = () => {
    if (user?.structureType === 'company') {
      return representatives.map((r) => ({ value: r.email || '', label: repToLabel(r) }));
    } else if (user?.structureType === 'user') {
      return [{ value: user.email, label: 'primary - ' + user.email }].concat(
        user?.secondaryEmails?.filter((e) => e && e != '').map((e) => ({ value: e, label: 'secondary - ' + e })) ?? [],
      );
    }
  };

  /* Simple update methods */
  const updateEmail = (e: { target: { value: React.SetStateAction<string> } }) => {
    if (manualEmail && manualEmail.length > 0) {
      setSelectedDropdown(null);
      setSelectedOption('free-text');
    }
    setManualEmail(e.target.value);
  };

  const onEmailBlur = () => {
    if (manualEmail && manualEmail.length > 0) {
      setSelectedDropdown(null);
    }
  };

  const updateRepresentative = (rep: { value: string; label: string } | null) => {
    setSelectedDropdown(rep);
    setManualEmail('');
  };

  const sendCall = async () => {
    setIsLoading(true);
    try {
      await sendMethod(selectedOption === 'dropdown' ? selectedDropdown?.value : manualEmail);
      setSelectedDropdown(null);
      setManualEmail('');
      close();
    } catch (e: any) {
      if (e.message.msg) {
        toast(e.message.msg, 'error');
      } else if (e.message) {
        toast(e.message, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const options = dropdownOptions();
  const isCompany = user?.structureType === 'company';
  const isDropDown = selectedOption === 'dropdown';

  const sendDisabled = isLoading || (selectedOption === 'dropdown' ? !selectedDropdown : manualEmail === '');

  const labels = {
    company: {
      title: 'Select a Representative or Provide an Email',
      select: 'Select a Representative',
    },
    user: {
      title: 'Select a Customer Email or Provide an Email',
      select: 'Select a Customer Email',
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      close={close}
      title="Send Payment Notification"
      actions={
        <>
          <Button className="button button-secondary" disabled={isLoading} onClick={close}>
            Cancel
          </Button>
          <Button className="button button-yellow" disabled={sendDisabled} onClick={sendCall}>
            Send
          </Button>
        </>
      }
    >
      <div className="pl-4 pr-4">
        <div className="mb-2">
          <Typography type="text-md" weight="semi-bold" className="mb-4">
            {labels[isCompany ? 'company' : 'user'].title}
          </Typography>
        </div>
        <div className="flex flex-col space-y-4">
          <div
            className={`p-4 border border-solid rounded-lg ${isDropDown ? 'border-blue-500' : 'border-gray-300'} ${
              !isDropDown ? 'opacity-50' : ''
            }`}
            onClick={() => setSelectedOption('dropdown')}
          >
            <div className="flex items-center">
              <input type="radio" name="notificationOption" checked={isDropDown} className="mr-2" />
              <SelectForm
                value={selectedDropdown}
                onChange={updateRepresentative}
                options={options}
                label={labels[isCompany ? 'company' : 'user'].select}
              />
            </div>
          </div>
          <div
            className={`p-4 border border-solid rounded-lg ${!isDropDown ? 'border-blue-500' : 'border-gray-300'} ${
              isDropDown ? 'opacity-50' : ''
            }`}
            onClick={() => setSelectedOption('free-text')}
          >
            <div className="flex items-center">
              <input
                type="radio"
                name="notificationOption"
                value="email"
                checked={selectedOption === 'free-text'}
                className="mr-2"
              />
              <div className="flex flex-col">
                <Typography type="text-sm" weight="medium" className="text-grey-700">
                  Enter an email address
                </Typography>
                <input
                  className="input mt-1"
                  placeholder="Email"
                  value={manualEmail}
                  onChange={updateEmail}
                  onBlur={onEmailBlur}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const getDeclineText = (payment: Payment): string | undefined => {
  if (payment.method === 'Stripe' && payment.gatewayResponse && payment.status === PaymentStatus.failed) {
    try {
      const gw = JSON.parse(payment.gatewayResponse);
      const declineText = gw.last_payment_error?.decline_code;
      return declineText
        .split('_')
        .map((w: string) => {
          return w[0].toUpperCase() + w.substring(1);
        })
        .join(' ');
    } catch (e: any) {
      return undefined;
    }
  }
};

export const PaymentStatusBadge = (payment: Payment) => {
  switch (payment.status) {
    case PaymentStatus.succeeded:
      return <StatusBadge label="Succeeded" type="grey" />;
    case PaymentStatus.failed: {
      const decline = getDeclineText(payment);
      return (
        <div title="Failed">
          <StatusBadge label={decline || 'Failed'} type="red" />
        </div>
      );
    }
    case PaymentStatus.pending: {
      let status: string = payment.status;
      if (payment.method === 'Stripe' && (payment.gatewayId?.startsWith('pi') || payment.gatewayId?.startsWith('py'))) {
        if (payment.gatewayStatus === 'requires_action') {
          status = '3DS Required';
        } else {
          status = _.startCase(payment.gatewayStatus?.replace('_', ' '));
        }
      }
      return <StatusBadge label={status} type="yellow" />;
    }
  }
};

export const InvoiceStatusBadge = (invoice: Invoice) => {
  let status, statusType: StatusBadgeType;
  if (invoice.documentType === 'Invoice') {
    switch (invoice.status) {
      case InvoiceStatus.Void:
        status = 'Void';
        statusType = 'red';
        break;
      case InvoiceStatus.Draft:
        status = 'Draft';
        statusType = 'grey';
        break;
      case InvoiceStatus.Approved:
        if (invoice.balance === 0) {
          status = 'Paid';
          statusType = 'green';
        } else if (invoice.balance > 0) {
          if (invoice.dueDate && invoice.dueDate.endOf('day').diffNow().milliseconds <= 0) {
            status = 'Past Due';
            statusType = 'red';
          } else {
            status = 'Payable';
            statusType = 'grey';
          }
        } else {
          status = 'Pending Refund';
          statusType = 'yellow';
        }
        break;
    }
  } else {
    switch (invoice.status) {
      case InvoiceStatus.Void:
        status = 'Void';
        statusType = 'red';
        break;
      case InvoiceStatus.Draft:
        status = 'Draft';
        statusType = 'grey';
        break;
      case InvoiceStatus.Approved:
        if (invoice.balance === 0) {
          status = 'Approved';
          statusType = 'green';
        } else {
          status = 'Approved';
          statusType = 'grey';
        }
    }
  }

  return <StatusBadge label={status} type={statusType} />;
};

const STRIPE_PAYMENT_ICONS: Record<string, JSX.Element> = {
  visa: <Icon icon="VisaCardLogo" size="xlarge" />,
  mastercard: <Icon icon="MasterCardLogo" size="xlarge" />,
  afterpay: <Icon icon="AfterPayLogo" size="xlarge" />,
};

export const PaymentMethodFormat = (method: string, methodDetail?: string) => {
  switch (method) {
    case 'Custom':
      return methodDetail;
    case 'Stripe': {
      if (methodDetail?.startsWith('ACH')) {
        return methodDetail;
      }
      const cardType = methodDetail?.trim().toLowerCase().split(' ')[0];
      return (
        <span className="flex items-center gap-2">
          {(cardType && STRIPE_PAYMENT_ICONS[cardType]) || cardType}
          {methodDetail
            ?.substring(cardType?.length || 0)
            .trim()
            .split(' ')
            .map((str, i) => (
              <span key={i}>{str.trim()}</span>
            ))}
        </span>
      );
    }
    default:
      return `${method} ${methodDetail || ''}`.trim();
  }
};
